import type { ReviewReportFullResponseType } from '@readme/api/src/mappings/branch/reviewReport/types';

import React, { useEffect } from 'react';

import useEnvInfo from '@core/hooks/useEnvInfo';
import useIsDocumentReady from '@core/hooks/useIsDocumentReady';
import useReadmeApi from '@core/hooks/useReadmeApi';
import type { APIErrorType } from '@core/types';

import { useSuperHubStore } from '..';

interface InitializeReviewReportProps {
  children: React.ReactNode;
  /**
   * Initial review report coming down from express during SSR.
   * @link packages/react-interop/middleware/hub/prepBranchReviewReport
   */
  initialReviewReport?: ReviewReportFullResponseType['data'];
}

export function InitializeReviewReport({ children, initialReviewReport }: InitializeReviewReportProps) {
  const { isServer } = useEnvInfo();
  const isReady = useIsDocumentReady();
  const [apiBaseUrl, currentBranch, isReviewReportReady, updateReviewReport] = useSuperHubStore(s => [
    s.getApiBaseUrl(),
    s.getCurrentChildBranch(),
    s.branches.reviewReport?.status === 'completed' || s.branches.reviewReport?.status === 'failed',
    s.branches.updateReviewReport,
  ]);

  /**
   * Indicates that the current branch has a review report to poll until it's
   * completed or failed. When no review report is present, no polling requests
   * should be made.
   */
  const isCurrentBranchReportAvailable = !!currentBranch?.review.report.id;

  const url = isCurrentBranchReportAvailable ? `/review/reports?report_id=${currentBranch.review.report.id}` : null;
  const { data } = useReadmeApi<ReviewReportFullResponseType, APIErrorType>(url, {
    apiBaseUrl,
    swr: {
      // Poll endpoint every X seconds until report is ready.
      refreshInterval: isReviewReportReady ? 0 : 5000,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  });

  // Update the initial review report immediately during SSR or on the initial
  // CSR so that our react tree downstream renders based on this state.
  if ((isServer || !isReady) && initialReviewReport) {
    updateReviewReport(initialReviewReport);
  }

  useEffect(() => {
    if (!data) return;
    updateReviewReport(data.data);
  }, [data, updateReviewReport]);

  return <>{children}</>;
}
